import { cloneElement, ReactElement, useMemo, useState } from 'react';
import { captureException } from '@sentry/react';
import { Link } from 'react-router-dom';
import { UploadProps, message, Typography, Space, Checkbox } from 'antd';
import { RcFile, UploadChangeParam } from 'antd/es/upload';
import IconUploadFile from 'components/IconUploadFile';
import apiRoutes from 'config/apiRoute';
import appRoutes from 'config/appRoutes';
import apiRequests from 'utils/api';
import asyncErrorHandler from 'utils/asyncErrorHandler';
import useWebSocket from 'utils/useWebSocket';
import { formatBytes, parseDateToUSFormat } from 'utils/string.utils';
import { FileViewerFile } from 'components/FileViewerModal';
import { UploadFileApi } from 'types/upload-type';
import moment from 'moment';

const thumbExtension = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'mp4', 'avi', 'mov', 'mkv', 'pdf'];

export const convertUploadToViewerFile = (file: UploadFileApi): FileViewerFile | null => {
  if (file.status !== 'done' || !file.response?.uuid) return null;
  return {
    uuid: file.response.uuid,
    extension: file.response.extension,
    name: file.name,
    size: String(file.size ?? 0),
    id: 0,
    type: 'attachment',
    height: null,
    width: null,
    uploadable_id: 0,
    uploadable_typ: '',
    created_at: file.response.created_at ?? '',
    updated_at: '',
    creator: null,
    editor: null,
    destroyer: null,
    url: !file.response.stated ? (file.url || file.response.url) : undefined,
  };
};

export interface UseFileUploadOptions {
  type: 'picture-card' | 'picture';
  uploads: UploadFileApi[];
  checkedFiles?: string[];
  hideButtonDeleteIds?: string[];
  setUploads: (file: UploadFileApi[]) => void;
  setLoading?: (loading: boolean) => void;
  onRequest?: (response: { url: string; uuid: string }) => Promise<unknown>;
  onRemove?: (file: UploadFileApi) => void;
  onCheckboxChange?: (checkedFiles: string[], checked: boolean, file: UploadFileApi) => void;
}

const useFileUpload = ({
  type,
  uploads,
  checkedFiles,
  hideButtonDeleteIds,
  setUploads,
  setLoading,
  onRequest,
  onRemove: onRemoveCustom,
  onCheckboxChange,
}: UseFileUploadOptions) => {
  const [failedToLoadIds, setFailedToLoadIds] = useState<string[]>([]);
  const [fileViewerOpen, setFileViewerOpen] = useState(false);
  const [fileViewerIndex, setFileViewerIndex] = useState(0);

  const viewerFiles = useMemo(
    () => uploads.map(convertUploadToViewerFile).filter((f): f is FileViewerFile => f !== null),
    [uploads],
  );

  const customRequest: UploadProps['customRequest'] = async (options) => {
    const { onSuccess, onError, onProgress, file } = options;

    const abort = new AbortController();

    const current = uploads.find((item) => item.uid === (file as RcFile).uid);

    if (current) current.abortController = abort;

    try {
      const response = await apiRequests.uploadFile({
        file: file as File,
        type: 'attachment',
        abortController: abort,
        onProgress,
      });

      if (onRequest) {
        const sentToApi: any = await onRequest(response);

        if (sentToApi) {
          response.url = response.url.replace('/tmp/', '/uploads/');
          response.stated = true;
        }
      }

      if (onSuccess) onSuccess(response);
    } catch (error) {
      if (error.code === 'ERR_CANCELED') return;

      if (onError) {
        onError({
          name: 'Upload failed',
          message: 'Upload failed',
        });
      }

      captureException(error);
    }
  };

  const onChange = ({ file, fileList }: UploadChangeParam<UploadFileApi>) => {
    const hasLoading = fileList.find((item) => item.status === 'uploading');

    setLoading?.(!!hasLoading);

    if (file.status === 'error') {
      message.error(`${file.name} file upload failed.`);
    }

    if (file.status === 'done' && file.response?.url) {
      file.url = file.response.url;
    }

    setUploads(fileList);
  };

  const onRemove: UploadProps['onRemove'] = (upload) => {
    if (onRemoveCustom) onRemoveCustom(upload);

    if (upload.response?.stated) {
      apiRequests.delete(`${apiRoutes.UPLOADS}/${upload.response.uuid}`).catch((error) => {
        asyncErrorHandler(error);
      });
    }
  };

  const onPreview = (file: UploadFileApi) => {
    if (file.status !== 'done' || !file.response?.uuid) return;
    const idx = viewerFiles.findIndex((f) => f.uuid === file.response!.uuid);
    if (idx === -1) return;
    setFileViewerIndex(idx);
    setFileViewerOpen(true);
  };

  const iconRender = (upload: UploadFileApi) => <IconUploadFile file={upload} />;

  const isImageUrl = (upload: UploadFileApi) => {
    if (failedToLoadIds.includes(upload.uid)) {
      return false;
    }

    if (upload.response && upload.response.stated) {
      return !!upload.thumbUrl && thumbExtension.includes(upload.response.extension);
    }

    return upload.type ? upload.type.startsWith('image') : false;
  };

  const updateImgComponent = (item: ReactElement, file: UploadFileApi) => {
    const [container, containerButtons, ...itemChildren] = item.props.children;
    const box = container.props.children;
    const [link, ...boxChildren] = box.props.children;
    const image = link.props.children;

    const newContainerButton = hideButtonDeleteIds?.includes(file.uid)
      ? cloneElement(containerButtons, containerButtons.props, containerButtons.props.children.slice(0, -1))
      : containerButtons;

    if (image.type !== 'img') {
      return cloneElement(item, item.props, [container, newContainerButton, ...itemChildren]);
    }

    const newImage = cloneElement(image, {
      ...image.props,
      onError: () => {
        setFailedToLoadIds((old) => [...old, file.uid]);
      },
    });

    return cloneElement(item, item.props, [
      cloneElement(
        container,
        { key: 'container-key', ...container.props },
        cloneElement(box, box.props, [cloneElement(link, { key: 'link-key', ...link.props }, newImage), ...boxChildren]),
      ),
      newContainerButton,
      ...itemChildren,
    ]);
  };

  const itemRender = (item: ReactElement, file: UploadFileApi) => {
    const isError = file.status === 'error';

    let targetItem = isError ? item.props.children : item;

    targetItem = file.status !== 'error' ? updateImgComponent(targetItem, file) : targetItem;

    const itemProps = item.props;

    if (type !== 'picture-card') {
      return isError ? cloneElement(item, itemProps, targetItem) : targetItem;
    }

    const [extension, ...rest] = file.name.split('.').reverse();

    const name = rest.length > 1 ? file.name.split('.').slice(0, -1).join('.') : rest.join();

    const linkDetail = (
      <div key="link-detail-key" style={{ lineHeight: 1.2, marginTop: file.status === 'uploading' ? 10 : 6, fontSize: 12 }}>
        <div>
          <Typography.Text type={isError ? 'danger' : undefined} ellipsis>
            {name}
          </Typography.Text>
        </div>

        <Space size={4} align="center" className={isError ? 'danger' : 'secondary'} style={{ width: '100%' }}>
          <span>{extension ? extension.toUpperCase() : ''}</span>
          <span style={{ fontSize: 18, lineHeight: 0, verticalAlign: 'middle' }}>&#x2022;</span>
          <span>{formatBytes(file.size ?? 0)}</span>
        </Space>

        {file.response?.created_at || file.response?.stated ? (
          <span className={isError ? 'danger' : 'secondary'}>{parseDateToUSFormat(file.response.created_at ?? moment())}</span>
        ) : undefined}
      </div>
    );

    const link =
      file.status === 'done' ? (
        <Link
          key="link-key"
          to={file.response?.stated ? `${appRoutes.FILE_VIEWER}/${file.response?.uuid}` : file.url ?? ''}
          target="_blank"
          style={{ display: 'block' }}
        >
          {linkDetail}
        </Link>
      ) : (
        linkDetail
      );

    const checkbox =
      checkedFiles && file.response?.stated ? (
        <div style={{ position: 'absolute', top: 0, left: 4, zIndex: 10 }}>
          <Checkbox
            checked={checkedFiles.includes(file.response.uuid)}
            onChange={(event) => {
              if (!file.response || !checkedFiles) return;

              const newCheckedFiles = event.target.checked
                ? [...checkedFiles, file.response.uuid]
                : checkedFiles.filter((uuid) => uuid !== file.response?.uuid);

              onCheckboxChange?.(newCheckedFiles, event.target.checked, file);
            }}
          />
        </div>
      ) : undefined;

    const percentBox =
      file.percent !== undefined && file.status === 'uploading' ? (
        <div
          style={{
            position: 'absolute',
            top: '40%',
            left: '50%',
            zIndex: 10,
            transform: 'translate(-50%, -50%)',
            fontSize: 26,
            fontWeight: 600,
            color: file.percent === 100 ? '#52c41a' : '#1890ff',
          }}
        >
          {file.percent.toFixed(0)}%
        </div>
      ) : undefined;

    const errorLabel = isError ? (
      <div
        style={{
          position: 'absolute',
          top: '40%',
          left: '50%',
          zIndex: 10,
          transform: 'translate(-50%, -50%)',
          fontSize: 26,
        }}
      >
        <Typography.Text type="danger">Failed</Typography.Text>
      </div>
    ) : undefined;

    targetItem = cloneElement(targetItem, targetItem.props, [
      ...targetItem.props.children,
      link,
      checkbox,
      percentBox,
      errorLabel,
    ]);

    return isError ? cloneElement(item, itemProps, targetItem) : targetItem;
  };

  useWebSocket({
    channelName: 'model.changes',
    listen: {
      event: '.app.models.foundation.upload',
      callback: (event: any) => {
        if (event.action !== 'delete') return;

        const newUploads = uploads.filter((upload) => upload.response?.uuid !== event.id);

        if (newUploads.length !== uploads.length) {
          setUploads(newUploads);
        }
      },
    },
  });

  return {
    customRequest,
    onChange,
    onRemove,
    onPreview,
    iconRender,
    isImageUrl,
    itemRender,
    fileViewerModalProps: {
      files: viewerFiles,
      initialIndex: fileViewerIndex,
      open: fileViewerOpen,
      onClose: () => setFileViewerOpen(false),
    },
  };
};

export default useFileUpload;
